"use client";

/**
 * TravelMock — animated mock of the askFinz Travel itinerary builder for
 * /apps/travel.
 *
 * Three-column layout: city tabs (L) → day-by-day cards (C) → budget
 * tracker (R). Top of frame: status pill "askFinz · planning Paris day 2".
 * A small stylised dotted-grid map sits under the cities header. When the
 * mock scrolls into view: status pill ticks, a new activity slides into
 * the Paris day-2 card, and the budget tracker updates with a brand-red
 * delta flicker on the running total.
 *
 * Mirrors the live travel.askfinz.ai itinerary builder; presentation-only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Plane,
  Hotel,
  Coffee,
  Camera,
  Sun,
  Moon,
  Train,
  Wallet,
  MapPin,
  Sparkles,
  ArrowUpRight,
  ChevronRight,
} from "lucide-react";

type City = { id: string; name: string; dates: string; active?: boolean };

const CITIES: City[] = [
  { id: "lon", name: "London", dates: "Mar 14 – 16" },
  { id: "par", name: "Paris",  dates: "Mar 16 – 19", active: true },
  { id: "lis", name: "Lisbon", dates: "Mar 19 – 22" },
];

type Slot = "morning" | "afternoon" | "evening";
type Activity = {
  slot: Slot;
  icon: typeof Coffee;
  title: string;
  transport?: { mode: "train" | "metro" | "walk"; detail: string };
  cost: number;
  inserted?: boolean;
};

type Day = {
  label: string;
  date: string;
  activities: Activity[];
};

const DAYS_INITIAL: Day[] = [
  {
    label: "Day 1",
    date: "Mar 16 · Sat",
    activities: [
      { slot: "morning",   icon: Coffee, title: "Café de Flore breakfast", cost: 22 },
      { slot: "afternoon", icon: Camera, title: "Louvre — booked 14:00",   transport: { mode: "metro", detail: "M1 → Palais Royal" }, cost: 32 },
      { slot: "evening",   icon: Hotel,  title: "Check-in · Hôtel Saint-Marc", cost: 184 },
    ],
  },
  {
    label: "Day 2",
    date: "Mar 17 · Sun",
    activities: [
      { slot: "morning",   icon: Coffee, title: "Marché des Enfants", cost: 18 },
      { slot: "afternoon", icon: Camera, title: "Musée d'Orsay",  transport: { mode: "walk", detail: "12 min" }, cost: 16 },
    ],
  },
];

// The activity that gets "dragged in" mid-animation onto Day 2 · evening.
const INSERTED: Activity = {
  slot: "evening",
  icon: Moon,
  title: "Seine sunset cruise · 19:30",
  transport: { mode: "metro", detail: "M12 → Solférino" },
  cost: 38,
  inserted: true,
};

const SLOT_META: Record<Slot, { icon: typeof Sun; label: string }> = {
  morning:   { icon: Sun,  label: "AM" },
  afternoon: { icon: Sun,  label: "PM" },
  evening:   { icon: Moon, label: "PM" },
};

const TRANSPORT_ICON: Record<"train" | "metro" | "walk", typeof Train> = {
  train: Train,
  metro: Train,
  walk:  ChevronRight,
};

const BUDGET_TOTAL = 2400;

export function TravelMock() {
  const [days, setDays] = useState<Day[]>(DAYS_INITIAL);
  const [pulse, setPulse] = useState(false);
  const [status, setStatus] = useState("Planning Paris · day 2");
  const [activeCity, setActiveCity] = useState("par");
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  // Sum of every activity cost currently on the board.
  const spent = days.reduce(
    (acc, d) => acc + d.activities.reduce((a, x) => a + x.cost, 0),
    0
  );
  const remaining = BUDGET_TOTAL - spent;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      setDays(DAYS_INITIAL);
      setPulse(false);
      setStatus("Planning Paris · day 2");

      schedule(() => setStatus("Searching · evening activities"), 600);
      schedule(() => setStatus("Drafting · Seine sunset cruise"), 1500);

      schedule(() => {
        setDays((cur) =>
          cur.map((d, i) => (i === 1 ? { ...d, activities: [...d.activities, INSERTED] } : d))
        );
        setPulse(true);
      }, 2300);

      schedule(() => setStatus("Day 2 · ready to review"), 3100);
      schedule(() => setPulse(false), 4100);

      // Loop
      schedule(runCycle, 7000);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            travel.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <Sparkles className="h-3 w-3" />
            <span className="font-mono">askFinz · {status}</span>
          </span>
        </div>

        <div className="grid grid-cols-12 min-h-[480px]">
          {/* City tabs + mini map */}
          <div className="col-span-12 md:col-span-3 border-b md:border-b-0 md:border-r hairline bg-foreground/[0.02] p-3">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 px-1 mb-2">
              Itinerary · 8 days
            </div>
            <div className="space-y-1.5">
              {CITIES.map((c, i) => {
                const isActive = c.id === activeCity;
                return (
                  <div key={c.id}>
                    <button
                      type="button"
                      onClick={() => setActiveCity(c.id)}
                      className={
                        "w-full text-left rounded-md px-2.5 py-2 flex items-center gap-2 transition " +
                        (isActive ? "bg-foreground text-background" : "hover:bg-foreground/5 text-foreground/75")
                      }
                    >
                      <MapPin className="h-3.5 w-3.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium tracking-tight truncate">{c.name}</div>
                        <div className={"text-[10px] " + (isActive ? "text-background/65" : "text-foreground/65")}>
                          {c.dates}
                        </div>
                      </div>
                    </button>
                    {i < CITIES.length - 1 && (
                      <div className="flex items-center justify-center py-1 text-foreground/35">
                        <Plane className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Stylised dotted-grid mini map */}
            <div className="mt-4 rounded-lg border hairline bg-background p-3">
              <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 mb-2">Route</div>
              <MiniMap />
            </div>
          </div>

          {/* Day cards */}
          <div className="col-span-12 md:col-span-6 p-4 space-y-3 overflow-hidden">
            {days.map((d) => (
              <div key={d.label} className="rounded-xl border hairline bg-background overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b hairline bg-foreground/[0.02]">
                  <div className="font-medium tracking-tight text-[13px]">{d.label}</div>
                  <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                    {d.date}
                  </div>
                </div>
                <div className="divide-y hairline">
                  {d.activities.map((a, i) => {
                    const SlotIcon = SLOT_META[a.slot].icon;
                    const Icon = a.icon;
                    return (
                      <div
                        key={i}
                        className={
                          "flex items-start gap-3 px-3 py-2.5 " +
                          (a.inserted ? "bg-foreground/[0.04] animate-[blur-fade-in_0.6s_ease-out]" : "")
                        }
                      >
                        <div className="mt-0.5 inline-flex items-center gap-1.5 w-12 shrink-0 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                          <SlotIcon className="h-3 w-3" />
                          <span>{a.slot.slice(0, 3)}</span>
                        </div>
                        <Icon className="h-3.5 w-3.5 mt-0.5 text-foreground/65 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-foreground/85 truncate">{a.title}</div>
                          {a.transport && (
                            <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-foreground/65 font-mono">
                              {(() => {
                                const T = TRANSPORT_ICON[a.transport.mode];
                                return <T className="h-2.5 w-2.5" />;
                              })()}
                              <span>{a.transport.detail}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-[12px] font-mono text-foreground/70 shrink-0">€{a.cost}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Budget tracker */}
          <div className="col-span-12 md:col-span-3 border-t md:border-t-0 md:border-l hairline bg-foreground/[0.02] p-4">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Wallet className="h-3 w-3" />
              Budget
            </div>

            <div className="mt-4">
              <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65">Spent so far</div>
              <div
                className={
                  "mt-1 font-mono text-[28px] tracking-tight transition-colors duration-500 " +
                  (pulse ? "text-[hsl(var(--accent))]" : "text-foreground/90")
                }
              >
                €{spent.toLocaleString()}
              </div>
              <div className="mt-1 text-[11px] text-foreground/65 font-mono">of €{BUDGET_TOTAL.toLocaleString()}</div>
            </div>

            {/* Bar */}
            <div className="mt-4 h-2 rounded-full bg-foreground/10 overflow-hidden">
              <div
                className="h-full bg-foreground transition-all duration-700"
                style={{ width: `${Math.min(100, (spent / BUDGET_TOTAL) * 100)}%` }}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
              <div className="rounded-md border hairline bg-background px-2.5 py-2">
                <div className="text-foreground/65 text-[10px] tracking-[0.16em] uppercase">Remaining</div>
                <div className="mt-0.5 font-mono text-foreground/85">€{remaining.toLocaleString()}</div>
              </div>
              <div className="rounded-md border hairline bg-background px-2.5 py-2">
                <div className="text-foreground/65 text-[10px] tracking-[0.16em] uppercase">Days left</div>
                <div className="mt-0.5 font-mono text-foreground/85">6</div>
              </div>
            </div>

            <div className="mt-4 rounded-md border hairline bg-background px-2.5 py-2 text-[11px]">
              <div className="text-foreground/65 text-[10px] tracking-[0.16em] uppercase">Daily avg</div>
              <div className="mt-0.5 font-mono text-foreground/85">€{Math.round(spent / 2)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>
          Live at <span className="font-mono text-foreground/65">travel.askfinz.ai</span>
        </span>
      </div>
    </div>
  );
}

/**
 * MiniMap — a stylised dotted-grid placeholder. No real map library; a 9×5
 * grid of dots with three labelled "pins" sitting on grid intersections
 * and a thin path joining them.
 */
function MiniMap() {
  const cols = 9;
  const rows = 5;
  // Pin grid coordinates (col, row) for London / Paris / Lisbon.
  const pins: { x: number; y: number; label: string; lit?: boolean }[] = [
    { x: 6, y: 1, label: "LON" },
    { x: 4, y: 2, label: "PAR", lit: true },
    { x: 1, y: 4, label: "LIS" },
  ];
  const w = 100;
  const h = 60;
  const cx = (c: number) => (c / (cols - 1)) * w;
  const cy = (r: number) => (r / (rows - 1)) * h;

  return (
    <div className="relative h-[80px]">
      <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full" aria-hidden tabIndex={-1}>
        {/* Dot grid */}
        {Array.from({ length: cols }).map((_, c) =>
          Array.from({ length: rows }).map((_, r) => (
            <circle
              key={`${c}-${r}`}
              cx={cx(c)}
              cy={cy(r)}
              r={0.6}
              className="fill-foreground/20"
            />
          ))
        )}
        {/* Dashed path London → Paris → Lisbon */}
        <polyline
          points={pins.map((p) => `${cx(p.x)},${cy(p.y)}`).join(" ")}
          className="stroke-foreground/35 fill-none"
          strokeWidth={0.4}
          strokeDasharray="1.5 1.5"
        />
        {/* Pins */}
        {pins.map((p) => (
          <g key={p.label}>
            <circle
              cx={cx(p.x)}
              cy={cy(p.y)}
              r={1.6}
              className={p.lit ? "fill-foreground" : "fill-foreground/55"}
            />
            <text
              x={cx(p.x) + 2.5}
              y={cy(p.y) - 1.5}
              className={"fill-foreground/65 text-[3.2px] font-mono"}
            >
              {p.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
